import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { login, logout, getUserDetails, resetPassword, register, resetPasswordConfirm, changePassword, getOrganisationMembers, setActivePortfolio, requestMagicLink, verifyMagicLink } from "../services/authApi"

export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      login(email, password),
    onSuccess: () => {
      // Invalidate and refetch user details after successful login
      queryClient.invalidateQueries({ queryKey: ["userDetails"] })
    },
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Clear all queries on logout
      queryClient.clear()
    },
  })
}

export const useUserDetails = () => {
  return useQuery({
    queryKey: ["userDetails"],
    queryFn: getUserDetails,
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => resetPassword(email),
  })
}

export const useResetPasswordConfirm = () => {
  return useMutation({
    mutationFn: (payload: {
      uid: string
      token: string
      newPassword1: string
      newPassword2: string
    }) => resetPasswordConfirm(payload),
  })
}

export const useChangePassword = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      oldPassword?: string
      newPassword1: string
      newPassword2: string
    }) => changePassword(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetails"] })
    },
  })
}

export const useRegister = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userData: {
      email: string
      password1: string
      password2: string
      first_name: string
      last_name: string
      company_name: string
      company_number: string
      organisation_type: string
    }) => register(userData),
    onSuccess: (data) => {
      // If registration is successful and returns a token, invalidate queries
      if (data?.data?.key) {
        queryClient.invalidateQueries({ queryKey: ["userDetails"] })
      }
    },
  })
}

export const useSetActivePortfolio = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (portfolioId: number) => setActivePortfolio(portfolioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetails"] })
    },
  })
}

export const useOrganisationMembers = () => {
  return useQuery({
    queryKey: ["organisationMembers"],
    queryFn: getOrganisationMembers,
    enabled: !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useRequestMagicLink = () => {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => requestMagicLink(email),
  })
}

export const useVerifyMagicLink = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ token }: { token: string }) => verifyMagicLink(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userDetails"] })
    },
  })
}
